<template>
  <div>
    <label>{{ label }}</label>
    <div
      :class="['check', { 'check--active' : checked }, { 'check--custom-text' : customText }, getTheme]"
      tabindex="0"
      @click="onClick"
      @keydown.enter="onClick"
    >
      <div class="check__circle" />
      <div
        v-if="!hasLink"
        :id="id + '-link'"
        class="check__text"
        @click="onClick"
      >
        {{ dataSource.Text }}
      </div>
      <a
        v-else
        :id="id"
        :class="['check__text check__text-link', { 'check__text--margin' : hasText }, { 'check__text--custom-text' : customText }]"
        :href="dataSource.Url"
        target="_blank"
        @keyup.enter="onEnter"
      >
        {{ dataSource.Text }}
      </a>
      <slot />
    </div>
    <div
      v-if="error"
      class="nt-form-error"
    >
      {{ error }}
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import emitter from 'tiny-emitter/instance'

export default {
  name: 'nt-checklink',
  props: {
    id: {
      type: String
    },
    label: {
      type: String,
      default: null
    },
    dataSource: {
      type: Object,
      required: true,
      validator(val) {
        return Object.hasOwnProperty.call(val, 'Text')
               && Object.hasOwnProperty.call(val, 'Value')
               && Object.hasOwnProperty.call(val, 'Url')
      }
    },
    validators: {
      type: Array,
      default: []
    },
    theme: {
      type: String,
      default: 'default'
    },
    customText: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      checked: false,
      error: null
    }
  },
  computed: {
    ...mapState('formStore', ['errors']),
    getTheme() {
      return `check--${this.theme}`
    },
    hasLink() {
      return !!this.dataSource.Url
    },
    hasText() {
      return !!this.dataSource.Text
    }
  },
  watch: {
    errors: {
      handler(values) {
        const { id } = this
        const error = values.find((value) => value[id]) || {}
        this.error = error[id]
      },
      deep: true
    }
  },
  created() {
    // Add to form initial values
    this.changeForm()
    // Add validators to form
    this.changeValidators({
      [this.id]: this.validators
    })
  },
  methods: {
    ...mapActions('formStore', ['changeFormData', 'changeValidators']),
    onEnter(event) {
      event.stopPropagation()
      window.open(this.dataSource.Url, '_blank')
    },
    onClick() {
      this.checked = !this.checked
      this.changeForm()
    },
    changeForm() {
      emitter.emit('checkLinkFormClick', { id: this.id, label: this.dataSource.Text })
      emitter.emit('changeForm', {
        [this.id]: this.checked ? this.dataSource.Value : null
      })
    }
  }
}
</script>

<style lang="scss" scoped>
  .check {
    $container: &;
    display: -webkit-inline-box;

    &:hover {
      cursor: pointer;
    }

    &__circle {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 1.125rem;
      height: 1.125rem;
      min-width: 1.125rem;
      min-height: 1.125rem;
      padding: 0.125rem;
      margin-right: 0.625rem;
      border: 2px solid $neutral-04;
    }

    & a {
      font-size: 1.125rem;
      line-height: 1.125rem;
    }

    &__text {
      font-size: 1rem;
      line-height: 1rem;
      letter-spacing: -0.0625rem;
      color: $neutral-03;
      text-decoration: none;

      &--margin {
        &:not(:last-child) {
          margin-right: 0.9375rem;
        }
      }

      &-link {
        &:hover {
          text-decoration: underline;
        }
      }
    }

    &--active {
      #{$container}__circle {
        &::before {
          content: '';
          width: 100%;
          height: 100%;
          background: $blue-04;
        }
      }
    }

    &--small {
      #{$container}__circle {
        min-width: 1rem;
        min-height: 1rem;
        width: 1rem;
        height: 1rem;
      }

      a {
        font-size: 1rem;
        line-height: 1rem;
      }

      #{$container}__text {
        font-size: 1rem;
        line-height: 1rem;
      }
    }

    &--custom-text {
      & .links {
        line-height: 1rem;
      }
      & * {
        letter-spacing: -0.0625rem;
        color: $neutral-03;
        text-decoration: none;

        &:hover {
          text-decoration: underline;
        }
      }
      #{$container}__text {
        display: none;
      }
    }
  }
</style>
